import React from "react";
import styled from "styled-components";
import Dial from "./Dial";

interface FXModuleProps {
  name: string;
  mixValue: number;
  onMixChange: (value: number) => void;
  color: string;
  onDrawerOpen: () => void;
  label?: string;
  valueFormatter?: (value: number) => string;
  onGraphClick?: () => void;
  isPerStepMode?: boolean;
  isMappingActive?: boolean;
  hasMappingApplied?: boolean;
  hideValue?: boolean;
  children?: React.ReactNode; // Optional children to render between controls and buttons
}

const FXModule: React.FC<FXModuleProps> = ({
  name,
  mixValue,
  onMixChange,
  color,
  onDrawerOpen,
  label = "Amount",
  valueFormatter,
  onGraphClick,
  isPerStepMode = false,
  isMappingActive = false,
  hasMappingApplied = false,
  hideValue = false,
  children,
}) => {
  const mappingToggleId = `fx-mapping-toggle-${name.toLowerCase()}`;

  // Format percentage for display (default formatter)
  const formatPercentage = (value: number) => {
    return `${Math.round(value * 100)}%`;
  };

  // Create a wrapper formatter that returns "Custom" when mapping is applied
  const createDialValueFormatter = () => {
    const baseFormatter = valueFormatter || formatPercentage;
    return (value: number) => {
      if (hasMappingApplied) {
        return "Custom";
      }
      return baseFormatter(value);
    };
  };

  const dialValueFormatter = createDialValueFormatter();

  return (
    <FXModuleContainer $isMappingActive={isMappingActive}>
      <ModuleTitle $isMappingActive={isMappingActive}>{name}</ModuleTitle>

      <ModuleControls>
        <StyledDialWrapper>
          <Dial
            value={mixValue}
            min={0}
            max={1}
            onChange={onMixChange}
            label={label}
            color={color}
            valueFormatter={dialValueFormatter}
            size={40}
            disabled={hasMappingApplied}
            hideValue={hideValue}
            showCustomStyle={hasMappingApplied}
          />
        </StyledDialWrapper>
      </ModuleControls>

      {children && <ModuleChildren>{children}</ModuleChildren>}

      <ModuleButtons>
        <IconButton
          id={mappingToggleId}
          data-mapping-toggle="true"
          onClick={onGraphClick || (() => {})}
          aria-label="View graph"
          $isActive={isMappingActive || hasMappingApplied}
          $isPerStepMode={isPerStepMode}
          $color="#F58413"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect
              x="2"
              y="9"
              width="2"
              height="4"
              fill="currentColor"
              opacity="0.6"
            />
            <rect
              x="6"
              y="6"
              width="2"
              height="7"
              fill="currentColor"
              opacity="0.6"
            />
            <rect
              x="10"
              y="4"
              width="2"
              height="9"
              fill="currentColor"
              opacity="0.6"
            />
          </svg>
        </IconButton>

        <IconButton onClick={onDrawerOpen} aria-label="Open settings">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <line
              x1="2"
              y1="4"
              x2="14"
              y2="4"
              stroke="currentColor"
              strokeWidth="1.5"
              opacity="0.6"
            />
            <circle cx="5.5" cy="4" r="1.5" fill="currentColor" />
            <line
              x1="2"
              y1="8"
              x2="14"
              y2="8"
              stroke="currentColor"
              strokeWidth="1.5"
              opacity="0.6"
            />
            <circle cx="10.5" cy="8" r="1.5" fill="currentColor" />
            <line
              x1="2"
              y1="12"
              x2="14"
              y2="12"
              stroke="currentColor"
              strokeWidth="1.5"
              opacity="0.6"
            />
            <circle cx="7" cy="12" r="1.5" fill="currentColor" />
          </svg>
        </IconButton>
      </ModuleButtons>
    </FXModuleContainer>
  );
};

// Styled Components
const FXModuleContainer = styled.div.attrs({
  id: "fx-module-container",
})<{ $isMappingActive?: boolean }>`
  padding: 16px 12px 12px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  border: 2px solid ${(props) => (props.$isMappingActive ? "#F58413" : "#ccc")};
  border-radius: 8px;
  margin-top: 2px;

  height: 200px;
  box-sizing: border-box;
  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
    margin-top: 12px;
  }
`;

const ModuleTitle = styled.h3.attrs({
  id: "fx-module-title",
})<{ $isMappingActive?: boolean }>`
  margin: 0;
  font-size: 14px;
  font-weight: bold;
  color: ${(props) => (props.$isMappingActive ? "#F58413" : "#333")};
  text-align: center;
  letter-spacing: 0.5px;
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: #e2e1dd;
  padding: 0 12px;
  border-radius: 4px;
`;

const ModuleControls = styled.div.attrs({
  id: "fx-module-controls",
})`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: auto;
  margin-top: 8px;
  align-items: center;
`;

const StyledDialWrapper = styled.div.attrs({
  id: "fx-module-dial-wrapper",
})``;

const ModuleChildren = styled.div.attrs({
  id: "fx-module-children",
})`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 0;
`;

const ModuleButtons = styled.div.attrs({
  id: "fx-module-buttons",
})`
  display: flex;
  gap: 6px;
  margin-top: 10px;
  justify-content: center;
  width: auto;
  flex-shrink: 0;
`;

const IconButton = styled.button<{
  $isActive?: boolean;
  $isPerStepMode?: boolean;
  $color?: string;
}>`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: 2px solid ${(props) => (props.$isActive ? "#F58413" : "#ccc")};
  border-radius: 8px;
  background: ${(props) => (props.$isActive ? "#F58413" : "transparent")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  padding: 0;
  box-shadow: ${(props) =>
    props.$isActive ? "0 0 4px rgba(245, 132, 19, 0.4)" : "none"};
  flex-shrink: 0;

  &:hover {
    background: ${(props) => (props.$isActive ? "#F58413" : "transparent")};
    color: ${(props) => (props.$isActive ? "#fff" : "#333")};
    border-color: ${(props) => (props.$isActive ? "#F58413" : "#bbb")};
  }

  &:active {
    transform: scale(0.96);
    background: ${(props) => (props.$isActive ? "#F58413" : "transparent")};
  }

  svg {
    width: 16px;
    height: 16px;
    opacity: ${(props) => (props.$isActive ? "1" : "0.6")};
    fill: ${(props) => (props.$isActive ? "#fff" : "currentColor")};
  }
`;

export default FXModule;
